<script>
import { VCardTitle, VDataTable, VSnackbar } from 'vuetify/lib'
import Mobile from 'vuetify/lib/mixins/mobile'
import debounce from 'lodash.debounce'
import TableCell from './TableCell.vue'
import TableConstructor from './TableConstructor.vue'
import TableColumnHeader from './TableColumnHeader.vue'
import {
  columnLayout, columnVariables, columnWidth, mergeColumns, saveableColumns, visibleColumns
} from './columns'
import { readTablePreferences, writeTablePreferences } from '@/services/tablePreferences'

// Preserve the existing keyboard row activation alongside Vuetify's native row rendering.
const KeyboardDataTable = VDataTable.extend({
  props: { keyboardRows: { type: Boolean, default: false } },
  methods: {
    genDefaultSimpleRow (item, index, classes) {
      const row = VDataTable.options.methods.genDefaultSimpleRow.call(this, item, index, classes)
      if (!this.keyboardRows) return row
      row.data.attrs = { ...row.data.attrs, tabindex: 0 }
      row.data.class = [row.data.class, 'keyboard-navigation-row']
      row.data.on.keydown = event => {
        if (event.defaultPrevented || event.key !== 'Enter' || event.repeat || event.target !== event.currentTarget) return
        this.$emit('keydown:row', item, this.createItemProps(item, index), event)
        event.preventDefault()
      }
      return row
    }
  }
})

export default {
  name: 'DataTable',
  mixins: [Mobile],
  provide () {
    return {
      tableCellHover: { state: this.hoverState, resume: this.resumeCellHover, suspend: this.suspendHover }
    }
  },
  inheritAttrs: false,
  props: {
    headers: { type: Array, default: () => [] },
    preferenceKey: { type: String, default: '' },
    constructorActivator: { type: String, default: '' },
    copyCells: { type: Boolean, default: true },
    itemRoute: { type: Function, default: null },
    keyboardRows: { type: Boolean, default: false },
    mobileBreakpoint: { type: [Number, String], default: 600 }
  },
  data () {
    return {
      savedColumns: [], measuredWidths: {}, storageFailed: false, resizingColumn: null,
      hoverState: { suspended: false }
    }
  },
  computed: {
    columns () { return mergeColumns(this.headers, this.savedColumns) },
    displayedColumns () { return visibleColumns(this.columns) },
    pinnedBoundaries () {
      return {
        left: [...this.displayedColumns].reverse().find(column => column.pin === 'left')?.value ?? null,
        right: this.displayedColumns.find(column => column.pin === 'right')?.value ?? null
      }
    },
    layout () { return columnLayout(this.displayedColumns, this.measuredWidths) },
    visibilitySignature () {
      return JSON.stringify(this.columns.map(({ value, visible, filterKeys }) => ({ value, visible, filterKeys })))
    }
  },
  watch: {
    preferenceKey: {
      immediate: true,
      handler (key) {
        this.endColumnResize(false)
        if (this.persist) this.persist.flush()
        this.savedColumns = readTablePreferences(key)
        this.measuredWidths = {}
      }
    },
    visibilitySignature: {
      immediate: true,
      handler () { this.$emit('columns-change', this.columns) }
    }
  },
  created () {
    // Preview widths stay outside Vue's reactive state until the gesture ends.
    this.resizeSession = null
    this.resizeFrame = null
    this.hoverPointer = null
    this.hoverResumeAfter = 0
    this.persist = debounce((key, columns) => {
      try { writeTablePreferences(key, columns) } catch (_) { this.storageFailed = true }
    }, 250)
    this.flushPreferences = () => this.persist.flush()
  },
  mounted () {
    window.addEventListener('scroll', this.suspendCellHover, { capture: true, passive: true })
    window.addEventListener('beforeunload', this.flushPreferences)
    window.addEventListener('pagehide', this.flushPreferences)
    if (window.ResizeObserver) {
      this.resizeObserver = new window.ResizeObserver(this.scheduleMeasure)
      this.resizeObserver.observe(this.$el)
    }
    this.scheduleMeasure()
  },
  updated () { this.scheduleMeasure() },
  beforeDestroy () {
    this.endColumnResize()
    window.removeEventListener('scroll', this.suspendCellHover, true)
    this.persist.flush()
    this.persist.cancel()
    window.removeEventListener('beforeunload', this.flushPreferences)
    window.removeEventListener('pagehide', this.flushPreferences)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.measureFrame) cancelAnimationFrame(this.measureFrame)
  },
  methods: {
    suspendHover () {
      this.hoverResumeAfter = Date.now() + 120
      this.hoverState.suspended = true
    },
    suspendCellHover (event) {
      const target = event.target
      const table = this.$refs.table?.$el
      if (!table) return
      const ancestorScroll = target === document || target === window ||
        (target instanceof Element && target.contains(this.$el))
      const tableScroll = target instanceof Element && table.contains(target) &&
        target.classList.contains('v-data-table__wrapper')
      // Detached menus and their own scrollable text must remain usable.
      if (!ancestorScroll && !tableScroll) return
      this.suspendHover()
    },
    resumeCellHover (event) {
      if (this.resizingColumn !== null) return
      if (event.type === 'mouseenter' || event.type === 'mousemove') {
        const previous = this.hoverPointer
        this.hoverPointer = { x: event.clientX, y: event.clientY }
        const moved = previous && (previous.x !== event.clientX || previous.y !== event.clientY)
        if (!moved || Date.now() < this.hoverResumeAfter) return
      }
      this.hoverState.suspended = false
    },
    setColumns (columns) {
      this.savedColumns = saveableColumns(columns)
      this.persist(this.preferenceKey, this.savedColumns)
    },
    resetColumns () { this.setColumns(mergeColumns(this.headers)) },
    resizeColumn (value, width) {
      const session = this.resizeSession
      if (!session || session.column.value !== value) return
      session.column.width = columnWidth(session.column, width)
      if (!this.resizeFrame) this.resizeFrame = requestAnimationFrame(this.applyResize)
    },
    startColumnResize (value) {
      // A full-width table may stretch columns beyond their saved widths.
      const widths = this.measureColumns()
      const columns = this.columns.map(column => ({
        ...column,
        width: widths[column.value] ? columnWidth(column, widths[column.value]) : column.width
      }))
      const header = [...this.$el.querySelectorAll('th[data-column-key]')]
        .find(element => element.dataset.columnKey === value)
      const session = {
        column: columns.find(column => column.value === value),
        key: this.preferenceKey,
        columns,
        visible: visibleColumns(columns),
        availableWidth: this.$refs.table.$el.querySelector('.v-data-table__wrapper').clientWidth,
        handle: header?.querySelector('.table-column-resize')
      }
      this.resizeSession = session
      if (this.measureFrame) cancelAnimationFrame(this.measureFrame)
      this.measureFrame = null
      this.resizingColumn = value
      this.suspendHover()
      this.$nextTick(() => {
        if (this.resizeSession === session) this.applyResize()
      })
    },
    resizedWidths (session) {
      const total = session.visible.reduce((sum, column) => sum + column.width, 0)
      const scale = total ? Math.max(1, session.availableWidth / total) : 1
      return Object.fromEntries(session.visible.map(column => [column.value, column.width * scale]))
    },
    applyLayout (variables) {
      for (const [name, value] of Object.entries(variables)) this.$el.style.setProperty(name, value)
    },
    applyResize () {
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = null
      const session = this.resizeSession
      if (!session) return
      const widths = this.resizedWidths(session)
      const columns = session.columns.map(column => widths[column.value]
        ? { ...column, width: widths[column.value] } : column)
      this.applyLayout(columnVariables(visibleColumns(columns)))
      session.handle?.setAttribute('aria-valuenow', String(session.column.width))
      return columns
    },
    endColumnResize (commit = true) {
      const session = this.resizeSession
      if (!session) return
      const columns = commit && session.key === this.preferenceKey ? this.applyResize() : null
      if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame)
      this.resizeFrame = null
      this.resizeSession = null
      this.resizingColumn = null
      if (columns) {
        this.measuredWidths = this.measureColumns()
        this.setColumns(columns.map(column => ({ ...column, width: columnWidth(column) })))
        this.persist.flush()
      } else {
        this.$nextTick(() => {
          if (this._isDestroyed) return
          // Vue skips unchanged model values, so undo the imperative preview explicitly.
          this.applyLayout(this.layout.variables)
          const column = this.columns.find(item => item.value === session.column.value)
          if (column) session.handle?.setAttribute('aria-valuenow', String(column.width))
        })
      }
      this.$nextTick(this.scheduleMeasure)
    },
    measureColumns () {
      const widths = {}
      this.$el.querySelectorAll('th[data-column-key]').forEach(element => {
        widths[element.dataset.columnKey] = element.getBoundingClientRect().width
      })
      return widths
    },
    scheduleMeasure () {
      if (this.resizeSession || this.measureFrame || this._isDestroyed) return
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null
        if (this.isMobile || this.resizeSession) return
        const widths = this.measureColumns()
        if (JSON.stringify(widths) !== JSON.stringify(this.measuredWidths)) this.measuredWidths = widths
      })
    },
    columnStyle (column) {
      return this.layout.styles[column.value]
    },
    columnClasses (column) {
      return {
        'table-column--pinned': !!column.pin,
        'table-column--resizing': this.resizingColumn === column.value,
        'table-column--left-pinned-edge': column.pin === 'left' && column.value === this.pinnedBoundaries.left,
        'table-column--right-pinned-edge': column.pin === 'right' && column.value === this.pinnedBoundaries.right
      }
    },
    constructorNode (h) {
      return h(TableConstructor, {
        props: { columns: this.columns, activator: this.constructorActivator },
        on: { change: this.setColumns, reset: this.resetColumns }
      })
    },
    toolbarNode (h, props, { title, actions }) {
      if (this.constructorActivator && !title && !actions) return null
      return h(VCardTitle, { class: 'table-toolbar' }, [
        title ? h('div', { class: 'table-toolbar__title' }, title(props)) : null,
        h('div', { class: 'table-toolbar__actions' }, [
          this.constructorActivator ? null : this.constructorNode(h),
          actions ? actions(props) : null
        ])
      ])
    },
    headerNode (h, { props, on }) {
      return h('thead', { class: 'v-data-table-header' }, [h('tr',
        this.displayedColumns.map(column => h(TableColumnHeader, {
          key: column.value,
          class: this.columnClasses(column),
          attrs: { 'data-column-key': column.value },
          props: {
            header: column,
            options: props.options,
            disableSort: props.disableSort,
            columnStyle: this.columnStyle(column)
          },
          on: {
            sort: on.sort,
            resize: width => this.resizeColumn(column.value, width),
            'resize-start': () => this.startColumnResize(column.value),
            'resize-end': this.endColumnResize
          },
          scopedSlots: this.$scopedSlots[`header.${column.value}`]
            ? { default: () => this.$scopedSlots[`header.${column.value}`]({ header: column }) } : {}
        }))
      )])
    }
  },
  render (h) {
    const { title, actions, filters, top, ...scopedSlots } = this.$scopedSlots
    if (!this.constructorActivator || title || actions || filters || top) {
      scopedSlots.top = props => [
        this.toolbarNode(h, props, { title, actions }),
        top ? top(props) : null,
        filters ? filters(props) : null
      ]
    }
    this.displayedColumns.forEach(column => {
      const slotName = `item.${column.value}`
      const originalSlot = this.$scopedSlots[slotName]
      scopedSlots[slotName] = props => {
        const content = originalSlot ? originalSlot(props) : [props.value == null ? '' : String(props.value)]
        const cell = h(TableCell, {
          props: {
            truncate: column.truncate !== false,
            copyable: this.copyCells && column.copyable !== false,
            linkTo: this.itemRoute && column.linkable !== false ? this.itemRoute(props.item) : null
          }
        }, content)
        return props.isMobile ? cell : h('td', {
          class: [column.cellClass, `text-${column.align || 'start'}`, this.columnClasses(column)],
          style: this.columnStyle(column)
        }, [cell])
      }
    })
    if (!this.isMobile) {
      scopedSlots.header = props => this.headerNode(h, props)
    }
    return h('div', {
      class: { 'table-container': true, 'table-container--resizing': this.resizingColumn !== null },
      style: this.layout.variables
    }, [
      this.constructorActivator ? this.constructorNode(h) : null,
      h(KeyboardDataTable, {
        ref: 'table',
        class: { 'configurable-data-table': true, 'linked-data-table': !!this.itemRoute },
        props: {
          headers: this.displayedColumns, keyboardRows: this.keyboardRows,
          mobileBreakpoint: this.mobileBreakpoint, hideDefaultHeader: !this.isMobile
        },
        // Vue 2 extracts props by deleting keys from attrs; retain inputs across local rerenders.
        attrs: { ...this.$attrs },
        on: { ...this.$listeners },
        scopedSlots
      }, this.$slots.default),
      h(VSnackbar, {
        props: { value: this.storageFailed, color: 'error', timeout: 5000 },
        on: { input: value => { this.storageFailed = value } }
      }, ['Не вдалося зберегти налаштування таблиці у браузері.'])
    ])
  }
}
</script>

<style lang="sass">
@import '~vuetify/src/styles/styles.sass'
@import '@/assets/sass/constants.sass'

.table-toolbar.v-card__title
  gap: 12px 16px

.table-toolbar__title
  flex: 1 1 auto
  min-width: 0

.table-toolbar__actions
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 8px
  margin-left: auto

.keyboard-navigation-row:focus-visible
  outline: 2px solid currentColor
  outline-offset: -2px

.keyboard-navigation-row
  cursor: pointer

.table-container--resizing
  cursor: col-resize
  user-select: none

.configurable-data-table:not(.v-data-table--mobile) > .v-data-table__wrapper > table
  table-layout: fixed
  width: var(--configurable-table-width)
  min-width: 100%

  > tbody > tr > td
    position: relative
    // Keep copy actions inside their cell's stacking context, below pinned columns.
    z-index: 0
    height: 40px
    padding: 0 !important

  > thead > tr > th
    height: 40px

  > thead > tr > th.table-column--pinned,
  > tbody > tr > td.table-column--pinned
    position: sticky
    z-index: 2

  > thead > tr > th.table-column--pinned
    z-index: 4
    background-color: $light-gray

  .table-column--left-pinned-edge
    box-shadow: 4px 0 8px -4px rgba(0, 0, 0, .2)

  .table-column--right-pinned-edge
    box-shadow: -4px 0 8px -4px rgba(0, 0, 0, .2)

  .table-column--resizing
    &::before, &::after
      content: ''
      position: absolute
      top: 0
      bottom: -1px
      width: 2px
      background-color: $primary
      pointer-events: none
      z-index: 3

    &::before
      left: 0

    &::after
      right: 0

+theme(configurable-data-table) using ($material)
  > .v-data-table__wrapper > table > tbody > tr
    > td.table-column--pinned
      background-color: map-get($material, 'cards')

    &:hover > td.table-column--pinned
      background-color: map-deep-get($material, 'table', 'hover')

    &.active > td.table-column--pinned, &.v-data-table__selected > td.table-column--pinned
      background-color: map-deep-get($material, 'table', 'active')

    &.table-row--rejected > td, &.table-row--rejected:hover > td
      background-color: mix(#ff0000, map-get($material, 'cards'), 20%)

.configurable-data-table.v-data-table--mobile
  > .v-data-table__wrapper > table
    display: block
    width: 100%

    > thead,
    > thead > tr,
    > thead > tr > th
      display: block
      width: 100%

    > tbody,
    > tbody > tr
      width: 100%

  .v-data-table__mobile-row__cell
    min-width: 0
    max-width: 65%

  .table-cell
    padding-right: 0
</style>
